/**
 * V97: CustomerMasterPanel - 고객사 마스터 관리 패널
 * V98: 고객사 클릭 → 프로젝트 목록 → Drawer 연결 기능 추가
 * V99: 고객사명 단일 필드화 (contact 필드 UI 제거)
 *
 * 고객사 목록 조회 / 생성 / 수정 / 아카이브 기능
 */

import React, { useState, useMemo, useEffect } from 'react';
import { Plus, Edit3, Trash2, X, Check, RefreshCw, Building2, ChevronDown, ChevronRight, ChevronLeft, ExternalLink, FileText, Calendar } from 'lucide-react';
import { Customer, CreateCustomerInput, UpdateCustomerInput, CustomerProjectSummary } from '../../../shared/types/customer';
import { useCustomerProjects } from '../../../shared/hooks/useCustomers';

interface CustomerMasterPanelProps {
  customers: Customer[];
  isLoading: boolean;
  onCreate: (input: CreateCustomerInput) => Promise<void>;
  onUpdate: (input: UpdateCustomerInput) => Promise<void>;
  onArchive: (id: string) => Promise<void>;
  isMutating: boolean;
  /** V98: 프로젝트 클릭 시 Drawer 열기 콜백 (projectId를 전달) */
  onOpenProject?: (projectId: string) => void;
}

export function CustomerMasterPanel({
  customers,
  isLoading,
  onCreate,
  onUpdate,
  onArchive,
  isMutating,
  onOpenProject,
}: CustomerMasterPanelProps) {
  // V99: 신규 생성 폼 상태 - 고객사명만
  const [isCreating, setIsCreating] = useState(false);
  const [newName, setNewName] = useState('');

  // V99: 편집 상태 - 고객사명만
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  // 에러/메시지
  const [error, setError] = useState<string | null>(null);

  // V104.1: 페이지네이션
  const ITEMS_PER_PAGE = 10;
  const [currentPage, setCurrentPage] = useState(1);

  // V98: 선택된 고객사 ID (프로젝트 목록 표시용)
  const [expandedCustomerId, setExpandedCustomerId] = useState<string | null>(null);

  // V98: 선택된 고객사의 프로젝트 목록 조회
  const {
    projects: customerProjects,
    isLoading: isLoadingProjects,
    error: projectsError,
  } = useCustomerProjects({
    customerId: expandedCustomerId || '',
    autoFetch: !!expandedCustomerId,
  });

  // V104.1: 최신 등록순 정렬 + 페이지네이션
  const sortedCustomers = useMemo(() => {
    return [...customers].sort((a, b) => {
      const getTime = (ts: any): number => {
        if (!ts) return 0;
        if (typeof ts.toMillis === 'function') return ts.toMillis();
        if (typeof ts.seconds === 'number') return ts.seconds * 1000;
        if (ts instanceof Date) return ts.getTime();
        return 0;
      };
      return getTime(b.createdAt) - getTime(a.createdAt);
    });
  }, [customers]);

  const totalPages = Math.ceil(sortedCustomers.length / ITEMS_PER_PAGE);
  const paginatedCustomers = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return sortedCustomers.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [sortedCustomers, currentPage]);

  // V104.1: customers 변경 시 페이지 초기화 (삭제/추가 시)
  useEffect(() => {
    if (currentPage > Math.ceil(customers.length / ITEMS_PER_PAGE)) {
      setCurrentPage(Math.max(1, Math.ceil(customers.length / ITEMS_PER_PAGE)));
    }
  }, [customers.length, currentPage]);

  const resetCreateForm = () => {
    setNewName('');
    setIsCreating(false);
    setError(null);
  };

  const resetEditForm = () => {
    setEditingId(null);
    setEditName('');
    setError(null);
  };

  // V99: 고객사 생성 - name만 전달
  const handleCreate = async () => {
    if (!newName.trim()) {
      setError('고객사명을 입력해주세요.');
      return;
    }

    try {
      setError(null);
      await onCreate({
        name: newName.trim(),
      });
      resetCreateForm();
    } catch (err: any) {
      setError(err?.message || '고객사 생성 중 오류가 발생했습니다.');
    }
  };

  const handleStartEdit = (customer: Customer) => {
    setEditingId(customer.id);
    setEditName(customer.name);
    setError(null);
  };

  // V99: 고객사 수정 - name만 전달
  const handleUpdate = async () => {
    if (!editingId || !editName.trim()) {
      setError('고객사명을 입력해주세요.');
      return;
    }

    try {
      setError(null);
      await onUpdate({
        id: editingId,
        name: editName.trim(),
      });
      resetEditForm();
    } catch (err: any) {
      setError(err?.message || '고객사 수정 중 오류가 발생했습니다.');
    }
  };

  const handleArchive = async (id: string, name: string) => {
    if (!window.confirm(`"${name}" 고객사를 아카이브하시겠습니까?\n아카이브된 고객사는 목록에서 숨겨집니다.`)) {
      return;
    }

    try {
      setError(null);
      await onArchive(id);
    } catch (err: any) {
      setError(err?.message || '고객사 아카이브 중 오류가 발생했습니다.');
    }
  };

  // V98: 고객사 행 클릭 → 프로젝트 목록 토글
  const handleToggleProjects = (customerId: string) => {
    if (expandedCustomerId === customerId) {
      setExpandedCustomerId(null);
    } else {
      setExpandedCustomerId(customerId);
    }
  };

  // V98: 프로젝트 클릭 → Drawer 열기
  const handleProjectClick = (projectId: string) => {
    if (onOpenProject) {
      onOpenProject(projectId);
    }
  };

  // V98: 프로젝트 상태 라벨
  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active': return '진행중';
      case 'completed': return '완료';
      case 'onHold': return '보류';
      case 'cancelled': return '취소';
      default: return status;
    }
  };

  // V98: 프로젝트 상태 색상
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return { bg: '#dcfce7', text: '#166534' };
      case 'completed': return { bg: '#dbeafe', text: '#1e40af' };
      case 'onHold': return { bg: '#fef3c7', text: '#92400e' };
      case 'cancelled': return { bg: '#fee2e2', text: '#dc2626' };
      default: return { bg: '#f1f5f9', text: '#64748b' };
    }
  };

  // V98: 프로젝트 목록 행 렌더링
  const renderProjectRow = (project: CustomerProjectSummary) => {
    const statusColor = getStatusColor(project.status);
    const contractPeriod = project.contractStartDate && project.contractEndDate
      ? `${project.contractStartDate} ~ ${project.contractEndDate}`
      : '-';

    return (
      <div
        key={project.id}
        onClick={() => handleProjectClick(project.id)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '10px 16px',
          marginLeft: '40px',
          background: '#fafafa',
          borderBottom: '1px solid #f1f5f9',
          cursor: onOpenProject ? 'pointer' : 'default',
          transition: 'background 0.15s',
        }}
        onMouseEnter={(e) => {
          if (onOpenProject) e.currentTarget.style.background = '#f1f5f9';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = '#fafafa';
        }}
      >
        <FileText size={14} style={{ color: '#94a3b8', flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '13px', fontWeight: 500, color: '#1e293b', marginBottom: '2px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span>{project.name}</span>
            {/* V69 (2026-05-20): 프로젝트 이력 Done 배지 (목록 일관성) */}
            {project.isHistorical === true && (
              <span
                style={{
                  padding: '2px 6px',
                  borderRadius: '8px',
                  fontSize: '10px',
                  fontWeight: 500,
                  background: '#f3e8ff',
                  color: '#6b21a8',
                  border: '1px solid #c084fc',
                }}
                title="프로젝트 이력 (실투입 직원 배정 없이 등급별 인원수만 기록)"
              >
                Done
              </span>
            )}
          </div>
          {/* V69 (2026-05-20): 프로젝트 이력은 조직 개념 없음 — 소속조직명 노출 차단('-'). */}
          <div style={{ fontSize: '12px', color: '#64748b' }}>
            {project.isHistorical === true ? '-' : project.ownerOrgUnitNameSnapshot}
          </div>
        </div>
        <span
          style={{
            padding: '2px 8px',
            fontSize: '11px',
            fontWeight: 500,
            background: statusColor.bg,
            color: statusColor.text,
            borderRadius: '4px',
            flexShrink: 0,
          }}
        >
          {getStatusLabel(project.status)}
        </span>
        <div style={{ fontSize: '12px', color: '#64748b', minWidth: '100px', textAlign: 'right' }}>
          {project.effectiveFrom || '-'}
        </div>
        <div style={{ fontSize: '12px', color: '#94a3b8', minWidth: '180px', textAlign: 'right' }}>
          <Calendar size={12} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
          {contractPeriod}
        </div>
        {onOpenProject && (
          <ExternalLink size={14} style={{ color: '#94a3b8', flexShrink: 0 }} />
        )}
      </div>
    );
  };

  return (
    <div style={{ padding: '0' }}>
      {/* 헤더 */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
        }}
      >
        <div>
          <h3 style={{ fontSize: '16px', fontWeight: 600, margin: 0 }}>고객사 마스터</h3>
          <p style={{ fontSize: '13px', color: '#64748b', margin: '4px 0 0' }}>
            프로젝트 생성 시 선택 가능한 고객사 목록을 관리합니다. 고객사를 클릭하면 연결된 프로젝트를 확인할 수 있습니다.
          </p>
        </div>
        {!isCreating && (
          <button
            onClick={() => setIsCreating(true)}
            disabled={isMutating}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              background: '#4f46e5',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: isMutating ? 'not-allowed' : 'pointer',
              fontSize: '13px',
              opacity: isMutating ? 0.7 : 1,
            }}
          >
            <Plus size={16} />
            고객사 추가
          </button>
        )}
      </div>

      {/* 에러 메시지 */}
      {error && (
        <div
          style={{
            padding: '12px 16px',
            marginBottom: '16px',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '6px',
            color: '#dc2626',
            fontSize: '13px',
          }}
        >
          {error}
        </div>
      )}

      {/* V99: 신규 생성 폼 - 고객사명만 */}
      {isCreating && (
        <div
          style={{
            padding: '16px',
            marginBottom: '16px',
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
          }}
        >
          <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px' }}>새 고객사</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <input
              type="text"
              placeholder="고객사명 *"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              style={{
                padding: '8px 12px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '14px',
              }}
              autoFocus
            />
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={resetCreateForm}
                disabled={isMutating}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '8px 16px',
                  background: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                }}
              >
                <X size={14} />
                취소
              </button>
              <button
                onClick={handleCreate}
                disabled={isMutating || !newName.trim()}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '8px 16px',
                  background: '#4f46e5',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isMutating || !newName.trim() ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  opacity: isMutating || !newName.trim() ? 0.7 : 1,
                }}
              >
                {isMutating ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
                생성
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 목록 */}
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
          <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite' }} />
          <p style={{ marginTop: '8px' }}>로딩 중...</p>
        </div>
      ) : sortedCustomers.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            padding: '40px',
            color: '#64748b',
            background: '#f8fafc',
            borderRadius: '8px',
          }}
        >
          <Building2 size={32} style={{ marginBottom: '8px', opacity: 0.5 }} />
          <p>등록된 고객사가 없습니다.</p>
        </div>
      ) : (
        <div
          style={{
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            overflow: 'hidden',
          }}
        >
          {/* V99: 테이블 - 고객사명 + 작업 컬럼만 */}
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: 600, borderBottom: '1px solid #e2e8f0', width: '32px' }}>
                  {/* V98: expand/collapse 아이콘 헤더 */}
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: 600, borderBottom: '1px solid #e2e8f0' }}>
                  고객사명
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '13px', fontWeight: 600, borderBottom: '1px solid #e2e8f0', width: '100px' }}>
                  작업
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedCustomers.map((customer, index) => (
                <React.Fragment key={customer.id}>
                  <tr
                    style={{
                      background: index % 2 === 0 ? 'white' : '#fafafa',
                      cursor: 'pointer',
                    }}
                    onClick={() => editingId !== customer.id && handleToggleProjects(customer.id)}
                  >
                    {editingId === customer.id ? (
                      // V99: 편집 모드 - 고객사명만
                      <>
                        <td style={{ padding: '8px 16px', borderBottom: '1px solid #e2e8f0' }}>
                          {/* 편집 중에는 expand 아이콘 숨김 */}
                        </td>
                        <td style={{ padding: '8px 16px', borderBottom: '1px solid #e2e8f0' }}>
                          <input
                            type="text"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.key === 'Enter' && handleUpdate()}
                            style={{
                              width: '100%',
                              padding: '6px 8px',
                              border: '1px solid #e2e8f0',
                              borderRadius: '4px',
                              fontSize: '13px',
                            }}
                            autoFocus
                          />
                        </td>
                        <td style={{ padding: '8px 16px', borderBottom: '1px solid #e2e8f0', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                            <button
                              onClick={(e) => { e.stopPropagation(); resetEditForm(); }}
                              disabled={isMutating}
                              style={{
                                padding: '6px',
                                background: 'white',
                                border: '1px solid #e2e8f0',
                                borderRadius: '4px',
                                cursor: 'pointer',
                              }}
                              title="취소"
                            >
                              <X size={14} />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleUpdate(); }}
                              disabled={isMutating || !editName.trim()}
                              style={{
                                padding: '6px',
                                background: '#4f46e5',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: isMutating || !editName.trim() ? 'not-allowed' : 'pointer',
                                opacity: isMutating || !editName.trim() ? 0.7 : 1,
                              }}
                              title="저장"
                            >
                              <Check size={14} />
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      // V99: 보기 모드 - 고객사명만
                      <>
                        <td style={{ padding: '12px 8px 12px 16px', borderBottom: '1px solid #e2e8f0' }}>
                          {/* V98: expand/collapse 아이콘 */}
                          {expandedCustomerId === customer.id ? (
                            <ChevronDown size={16} style={{ color: '#64748b' }} />
                          ) : (
                            <ChevronRight size={16} style={{ color: '#94a3b8' }} />
                          )}
                        </td>
                        <td style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0', fontSize: '14px', fontWeight: 500 }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {customer.name}
                            {/* V99.2: 아카이브 상태 배지 */}
                            {customer.status === 'archived' && (
                              <span
                                style={{
                                  padding: '2px 6px',
                                  fontSize: '11px',
                                  fontWeight: 500,
                                  background: '#f1f5f9',
                                  color: '#64748b',
                                  borderRadius: '4px',
                                }}
                              >
                                아카이브됨
                              </span>
                            )}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0', textAlign: 'center' }}>
                          {/* V99.2: archived 고객사는 수정/아카이브 버튼 숨김 */}
                          {customer.status === 'archived' ? (
                            <span style={{ fontSize: '12px', color: '#94a3b8' }}>-</span>
                          ) : (
                            <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleStartEdit(customer); }}
                                disabled={isMutating || editingId !== null}
                                style={{
                                  padding: '6px',
                                  background: 'white',
                                  border: '1px solid #e2e8f0',
                                  borderRadius: '4px',
                                  cursor: isMutating || editingId !== null ? 'not-allowed' : 'pointer',
                                  opacity: isMutating || editingId !== null ? 0.5 : 1,
                                }}
                                title="수정"
                              >
                                <Edit3 size={14} />
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleArchive(customer.id, customer.name); }}
                                disabled={isMutating || editingId !== null}
                                style={{
                                  padding: '6px',
                                  background: 'white',
                                  border: '1px solid #fecaca',
                                  borderRadius: '4px',
                                  cursor: isMutating || editingId !== null ? 'not-allowed' : 'pointer',
                                  opacity: isMutating || editingId !== null ? 0.5 : 1,
                                  color: '#dc2626',
                                }}
                                title="아카이브"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          )}
                        </td>
                      </>
                    )}
                  </tr>
                  {/* V98: 프로젝트 목록 (expanded) */}
                  {expandedCustomerId === customer.id && editingId !== customer.id && (
                    <tr>
                      <td colSpan={3} style={{ padding: 0, background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                        {isLoadingProjects ? (
                          <div style={{ padding: '16px', textAlign: 'center', color: '#64748b' }}>
                            <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite', marginRight: '8px' }} />
                            프로젝트 로딩 중...
                          </div>
                        ) : projectsError ? (
                          <div style={{ padding: '16px', textAlign: 'center', color: '#dc2626', fontSize: '13px' }}>
                            프로젝트 조회 중 오류: {projectsError.message}
                          </div>
                        ) : customerProjects.length === 0 ? (
                          <div style={{ padding: '16px 16px 16px 56px', color: '#64748b', fontSize: '13px' }}>
                            이 고객사에 연결된 프로젝트가 없습니다.
                          </div>
                        ) : (
                          <div>
                            {/* 프로젝트 목록 헤더 */}
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '8px 16px',
                                marginLeft: '40px',
                                background: '#f1f5f9',
                                fontSize: '12px',
                                fontWeight: 600,
                                color: '#64748b',
                                borderBottom: '1px solid #e2e8f0',
                              }}
                            >
                              <FileText size={14} style={{ visibility: 'hidden' }} />
                              <div style={{ flex: 1 }}>프로젝트명</div>
                              <div style={{ width: '60px', textAlign: 'center' }}>상태</div>
                              <div style={{ width: '100px', textAlign: 'right' }}>적용일</div>
                              <div style={{ width: '180px', textAlign: 'right' }}>계약기간</div>
                              {onOpenProject && <div style={{ width: '14px' }}></div>}
                            </div>
                            {customerProjects.map(renderProjectRow)}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>

          {/* V104.1: 페이지네이션 UI */}
          {totalPages > 1 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 0',
                borderTop: '1px solid #e2e8f0',
                backgroundColor: '#f8fafc',
              }}
            >
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  backgroundColor: currentPage === 1 ? '#f1f5f9' : '#fff',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  opacity: currentPage === 1 ? 0.5 : 1,
                }}
              >
                <ChevronLeft size={16} />
              </button>

              <span style={{ fontSize: '14px', color: '#374151', minWidth: '60px', textAlign: 'center' }}>
                {currentPage} / {totalPages}
              </span>

              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  backgroundColor: currentPage === totalPages ? '#f1f5f9' : '#fff',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  opacity: currentPage === totalPages ? 0.5 : 1,
                }}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* 안내 */}
      <p style={{ fontSize: '12px', color: '#94a3b8', marginTop: '12px' }}>
        * 아카이브된 고객사는 프로젝트 생성 시 선택할 수 없지만, 기존 프로젝트에는 영향 없습니다.
      </p>
    </div>
  );
}

export default CustomerMasterPanel;
